import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";
import { submitToWeb3Forms } from "../utils/web3forms";

/* ── Reusable wave divider ────────────────────────────── */
const WaveDivider: React.FC<{ topColor: string; bottomColor: string; flip?: boolean }> = ({
  topColor,
  bottomColor,
  flip = false,
}) => (
  <div style={{ background: topColor, lineHeight: 0 }}>
    <svg
      viewBox="0 0 1440 70"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      style={{ display: "block", width: "100%", transform: flip ? "scaleX(-1)" : undefined }}
    >
      <path
        d="M0,35 C240,70 480,0 720,35 C960,70 1200,0 1440,35 L1440,70 L0,70 Z"
        fill={bottomColor}
      />
    </svg>
  </div>
);

/* ── Animated counter ─────────────────────────────────── */
const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({
  end,
  suffix = "",
  duration = 2000,
}) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(end);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openFAQ, setOpenFAQ] = useState(0);
  const [formStatus, setFormStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const get = (key: string) => String(data.get(key) ?? "");
    setFormStatus("sending");
    const ok = await submitToWeb3Forms("New Home Page Enquiry", {
      name: `${get("firstName")} ${get("lastName")}`.trim(),
      email: get("email"),
      phone: get("phone"),
      service: get("service") || "Not specified",
      message: get("message"),
    });
    if (ok) {
      form.reset();
      setFormStatus("success");
    } else {
      setFormStatus("error");
    }
  };

  const slides = [
    {
      title: "Welcome To The Stables",
      subtitle: "Your Gateway to a Magical Horse Riding Experience!",
      description:
        "Experience the joy of horseback riding with our gentle, well-trained horses. Perfect for families, beginners, and adventure seekers. Discover the magic of connecting with these magnificent animals in our beautiful natural setting.",
      cta1: "Discover More",
      cta1Link: "/horse-rides",
      cta2: "Learn More",
      cta2Link: "/about",
      bookNowLink: "https://secure.activitybridge.com/book?activityid=15939",
      bookNowExternal: true,
    },
    {
      title: "Kids’ Pony Party",
      subtitle: "Fun, Laughter & Pony Rides!",
      description:
        "Make your child’s special day magical with pony rides, games, and outdoor fun. Our countryside venue is perfect for unforgettable kids’ parties filled with joy, giggles, and gentle horses.",
      cta1: "Party Packages",
      cta1Link: "/parties",
      cta2: "Party Packages",
      cta2Link: "/parties",
      bookNowLink:
        "mailto:info@thestableshorserides.co.za?subject=Stable Party Enquiry",
      bookNowExternal: false,
    },
    {
      title: "Romantic Picnic Ride",
      subtitle: "Love, Horses & Scenic Views",
      description:
        "Escape the city and reconnect in nature with a romantic horse ride followed by a private picnic. Ideal for anniversaries, proposals, or just because. Let us make your moment unforgettable.",
      cta1: "Explore Package",
      cta1Link: "/horse-ride-picnic",
      cta2: "Explore Package",
      cta2Link: "/horse-ride-picnic",
      bookNowLink:
        "https://secure.activitybridge.com/book?activityid=15941",
      bookNowExternal: true,
    },
  ];

  const faqItems = [
    {
      question: "What age groups can participate in horse riding?",
      answer:
        "We welcome riders of all ages! Pony rides are perfect for children aged 2-8 years, mini rides are suitable for ages 4+ with adults, and standard rides are available for ages 12+. All rides are guided by experienced staff to ensure safety.",
    },
    {
      question: "Do I need prior horse riding experience?",
      answer:
        "No prior experience is required! Our rides are designed for beginners and are guided by experienced staff. We provide all necessary safety equipment and instructions before your ride.",
    },
    {
      question: "What safety measures do you have in place?",
      answer:
        "Safety is our top priority. We provide helmets and all necessary safety equipment. All rides are guided by experienced staff, and our horses are carefully selected and well-trained. We maintain strict safety protocols throughout your experience.",
    },
    {
      question: "How do I book a horse riding experience?",
      answer:
        "You can book by calling us at +27 69 946 4074, emailing us at info@thestableshorserides.co.za, or using our contact form on the website. We recommend booking in advance, especially for weekends and holidays.",
    },
    {
      question: "What should I wear for horse riding?",
      answer:
        "We recommend comfortable, closed-toe shoes (no sandals or flip-flops), long pants, and weather-appropriate clothing. Avoid loose clothing that could get caught. We provide helmets and safety equipment.",
    },
    {
      question: "What happens if it rains on the day of my booking?",
      answer:
        "We monitor weather conditions closely. If weather conditions are unsafe for riding, we'll reschedule your booking at no additional cost. We'll contact you as early as possible if we need to reschedule.",
    },
    {
      question: "Can I bring my own food for the picnic?",
      answer:
        "Our picnic experiences include delicious food and refreshments. However, if you have specific dietary requirements or would like to bring additional items, please let us know in advance and we'll accommodate your needs.",
    },
    {
      question: "How long do the rides last?",
      answer:
        "Ride durations vary: Pony rides are 10 minutes, mini rides are 30 minutes, and standard rides are 60 minutes. Picnic experiences include additional time for the picnic portion of your experience.",
    },
    {
      question: "Can I take photos during my ride?",
      answer:
        "Yes! We encourage you to take photos to capture your memories. However, for safety reasons, we ask that you don't use flash photography near the horses. Our staff can also help take photos of your group.",
    },
    {
      question: "What's included in the party packages?",
      answer:
        "Our party packages include horse rides for all guests, a picnic setup with food and refreshments, party decorations, and supervision by our experienced staff. We can customize packages based on your group size and requirements.",
    },
    {
      question: "Is there a weight limit for riders?",
      answer:
        "We have horses suitable for riders of different sizes and weights. Please contact us to discuss your specific requirements, and we'll ensure we have the right horse for you.",
    },
    {
      question: "Can I feed the horses?",
      answer:
        "Yes! We provide treats for the horses, and our staff will show you the proper way to feed them. This is a great way to interact with the horses and learn more about them.",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 20000);
    return () => clearInterval(timer);
  }, [slides.length]);

  /* Scroll-reveal observer */
  useEffect(() => {
    const targets = document.querySelectorAll(
      ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Stables Horse Rides",
    "description": "Horse rides, romantic picnics, pony parties, photoshoots and proposal experiences in Pretoria East, Boschkop.",
    "url": "https://www.thestableshorserides.co.za",
    "telephone": "+27699464074",
    "email": "info@thestableshorserides.co.za",
    "image": "https://www.thestableshorserides.co.za/Stables5.jpg",
    "priceRange": "R175–R2200",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "R964",
      "addressLocality": "Boschkop",
      "addressRegion": "Pretoria East",
      "addressCountry": "ZA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -25.7888,
      "longitude": 28.4256
    },
    "sameAs": [
      "https://www.facebook.com/thestableshorserides",
      "https://www.instagram.com/thestables.horserides/"
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="The Stables | Horse Rides, Picnics & Parties Pretoria East"
        description="Experience magical horse rides, romantic picnics, pony parties, photoshoots and proposal experiences at The Stables in Pretoria East, Boschkop. Book online!"
        path="/"
        schema={localBusinessSchema}
      />
      {/* Hero Carousel Section */}
      <section className="relative h-screen overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true"
            className="w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
            preload="metadata"
            onLoadedMetadata={(e) => {
              const video = e.target as HTMLVideoElement;
              video.muted = true;
              video.play().catch(() => {
                // Fallback: if autoplay fails, show poster image
                console.log("Autoplay failed, showing poster image");
              });
            }}
            onCanPlay={(e) => {
              const video = e.target as HTMLVideoElement;
              if (video.paused) {
                video.play().catch(() => {
                  console.log("Video play failed");
                });
              }
            }}
          >
            <source src="/landingPage_web.mp4" type="video/mp4" />
            {/* Fallback image if video doesn't load */}
            <img
              src="Stables5.jpg"
              alt="Horse riding background"
              className="w-full h-full object-cover"
            />
          </video>
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 animate-fade-in leading-tight gradient-text-gold">
              {slides[currentSlide].title}
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-center animate-fade-in-delay text-white/90">
              {slides[currentSlide].description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center animate-fade-in-delay-2">
              <Link
                to={slides[currentSlide].cta1Link}
                className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 inline-flex items-center"
              >
                {slides[currentSlide].cta1}
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
              <a
                href={slides[currentSlide].bookNowLink}
                target={slides[currentSlide].bookNowExternal ? "_blank" : undefined}
                rel={slides[currentSlide].bookNowExternal ? "noopener noreferrer" : undefined}
                className="bg-stables-600 text-white hover:bg-stables-700 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 inline-flex items-center"
              >
                Book Now
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Social Media Icons - Left Side */}
        <div className="absolute left-16 top-1/2 transform -translate-y-1/2 z-20 hidden lg:block">
          <div className="flex flex-col space-y-4">
            <a
              href="https://www.facebook.com/thestableshorserides"
              className="text-white hover:text-stables-200 transition-colors duration-200"
            >
              <img
                src="facebook.png"
                alt="facebook"
                className="w-8 h-8 object-contain"
              />
            </a>
            <a
              href="https://www.instagram.com/thestables.horserides/"
              className="text-white hover:text-stables-200 transition-colors duration-200"
            >
              <img
                src="instagram.png"
                alt="instagram"
                className="w-8 h-8 object-contain"
              />
            </a>
            <a
              href="https://twitter.com"
              className="text-white hover:text-stables-200 transition-colors duration-200"
            >
              <img
                src="mail.png"
                alt="mail"
                className="w-8 h-8 object-contain"
              />
            </a>
          </div>
        </div>

        {/* Scroll Indicator - Right Side */}
        <div className="absolute right-16 top-1/2 transform -translate-y-1/2 z-20 hidden lg:block">
          <div className="flex flex-col items-center text-white">
            <span className="text-base text-lg font-medium transform rotate-90 mb-4">
              Scroll
            </span>
            <div className="w-0.5 h-12 bg-white animate-bounce mb-1 mt-4 ml-1 mr-1"></div>
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "bg-white scale-125"
                  : "bg-white/50 hover:bg-white/75"
              }`}
            ></button>
          ))}
        </div>
      </section>

      {/* Marquee Strip */}
      <div className="bg-stables-900 py-3 overflow-hidden border-y border-stables-700/50">
        <div className="marquee-track">
          {[
            "Horse Rides",
            "Romantic Picnics",
            "Pony Parties",
            "Photoshoots",
            "Proposals",
            "1000+ Happy Clients",
            "15+ Years Experience",
            "Scenic Trails",
            "30+ Happy Horses",
          ].concat([
            "Horse Rides",
            "Romantic Picnics",
            "Pony Parties",
            "Photoshoots",
            "Proposals",
            "1000+ Happy Clients",
            "15+ Years Experience",
            "Scenic Trails",
            "30+ Happy Horses",
          ]).map((item, i) => (
            <span
              key={i}
              className="flex-shrink-0 px-6 text-earth-300 text-sm font-medium tracking-wide"
            >
              {item}
              <span className="ml-6 text-stables-500">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* Statistics Section */}
      <section className="py-20 bg-gradient-to-br from-stables-600 to-stables-700">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { end: 15, suffix: "+", label: "Years Experience" },
              { end: 30, suffix: "+", label: "Happy Horses" },
              { end: 1000, suffix: "+", label: "Satisfied Clients" },
              { end: 5, suffix: "", label: "Riding Trails" },
            ].map(({ end, suffix, label }, i) => (
              <div key={i} className={`group relative scroll-reveal stagger-${i + 1}`}>
                <div className="absolute inset-0 bg-gradient-to-r from-earth-500 to-earth-600 rounded-3xl sm:rotate-1 sm:group-hover:rotate-2 transition-transform duration-300" />
                <div className="relative bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                  <div className="text-4xl md:text-5xl font-bold font-serif text-stables-800 mb-2">
                    <AnimatedCounter end={end} suffix={suffix} />
                  </div>
                  <div className="text-stables-600 text-base font-semibold">
                    {label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider topColor="#3a5a3a" bottomColor="#fdfbf7" />

      {/* Services Overview */}
      <section className="py-20 bg-earth-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-block bg-stables-100 text-stables-800 px-4 py-2 mb-6 rounded-full text-sm font-semibold tracking-wide scroll-reveal">
            Our Services
          </div>
          <div className="text-center mb-16 scroll-reveal stagger-1">
            <h2 className="text-4xl md:text-5xl font-bold font-serif gradient-text mb-6">
              Our Horse Riding Services
            </h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto text-center">
              Choose from our range of experiences designed for riders of all
              ages and skill levels
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Pony Rides */}
            <div className="group relative scroll-reveal stagger-1">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-pink-400 to-pink-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="ponyRide.jpg"
                    alt="Children riding horses"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Pony Rides For Kids
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R175 per child
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Perfect for kids aged 2 to 8, a guided 10-minute stables
                    stroll - great for nervous riders. Kids 8 and older? Try our
                    Mini Ride, parents welcome to walk beside.
                  </p>
                  <a
                    href="https://secure.activitybridge.com/book?activityid=15937"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>

            {/* Mini Ride */}
            <div className="group relative scroll-reveal stagger-2">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-blue-400 to-blue-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="miniRide.jpg"
                    alt="Family horse riding"
                    className="w-full h-48 aspect-1/1 group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Mini Ride For Adult & Kids
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R300 per child | R400 per adult
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Adventure awaits for all ages, starting at 4 years old.
                    Enjoy a 30-minute, 2km trail ride. Children up to 10 led by
                    a guide.
                  </p>
                  <a
                    href="https://secure.activitybridge.com/book?activityid=15938"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>

            {/* Standard Ride */}
            <div className="group relative scroll-reveal stagger-3">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-green-400 to-green-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="standardRide2.jpg"
                    alt="Adult horse riding"
                    className="w-full h-48 group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Standard Ride
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R650 per person
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Captivating rides for ages 12+, our 5km trail adventure
                    lasts 60 minutes. All rides proceed at a leisurely walking
                    pace.
                  </p>
                  <a
                    href="https://secure.activitybridge.com/book?activityid=15939"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>

            {/* Romantic Picnic */}
            <div className="group relative scroll-reveal stagger-4">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-pink-400 to-pink-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="picnic.jpg"
                    alt="Romantic picnic with horses"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Romantic Picnic
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R2200 per couple
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Ride, dine, and unwind under pecan trees. Enjoy a scenic
                    horseback ride, a romantic picnic with champagne, and
                    peaceful horse-watching.
                  </p>
                  <a
                    href="https://secure.activitybridge.com/book?activityid=15941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>

            {/* Everyday Picnic */}
            <div className="group relative scroll-reveal stagger-5">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-blue-400 to-blue-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="everyDayPicnic.jpg"
                    alt="Family picnic with horses"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Every Day Picnic
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R600 per child | R800 per adult
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    30-minute farm trail horse ride, followed by a picnic under
                    the trees with treats for you and your horse.
                  </p>
                  <a
                    href="https://secure.activitybridge.com/book?activityid=15940"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="group relative scroll-reveal stagger-6">
              <div
                className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                style={{
                  background:
                    "linear-gradient(135deg, from-green-400 to-green-600)",
                }}
              ></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="partiesImage.jpg"
                    alt="Party setup with horses"
                    className="w-full h-48 group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Stable Parties
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    Custom Pricing
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    We offer fun horse ride & picnic parties. We specialize in
                    creating memorable horse ride and picnic parties that
                    promise a day filled with joy and adventure.
                  </p>
                  <a
                    href="mailto:info@thestableshorserides.co.za?subject=Stable Party Enquiry"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Enquire Now
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Photoshoots & Proposals — centred row */}
          <div className="grid md:grid-cols-2 gap-8 mt-8 max-w-2xl mx-auto">
            {/* Photoshoots */}
            <div className="group relative">
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="Stables2.jpg"
                    alt="Horse photoshoot session"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Horse Photoshoots
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    R850 per session
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Capture stunning, timeless memories surrounded by our beautiful horses and scenic landscapes.
                  </p>
                  <a
                    href="mailto:info@thestableshorserides.co.za?subject=Horse Photoshoot Enquiry"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Enquire Now
                  </a>
                </div>
              </div>
            </div>

            {/* Proposals */}
            <div className="group relative">
              <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 h-full flex flex-col">
                <div className="overflow-hidden rounded-2xl mb-6">
                  <img
                    src="proposals/proposal2.jpg"
                    alt="Romantic horse proposal setup"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-stables-800 mb-3">
                    Horse Proposals
                  </h3>
                  <p className="text-stables-600 font-semibold text-xl mb-4">
                    Custom Pricing
                  </p>
                  <p className="text-stables-700 mb-6 leading-relaxed flex-1">
                    Pop the question in the most magical setting imaginable — surrounded by horses, nature, and love.
                  </p>
                  <a
                    href="mailto:info@thestableshorserides.co.za?subject=Proposal Experience Enquiry"
                    className="inline-block bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Enquire Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider topColor="#fdfbf7" bottomColor="#ffffff" flip />

      {/* About Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-6 text-center md:text-left scroll-reveal-left">
              <div className="inline-block bg-stables-100 text-stables-800 px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                About The Stables
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-serif gradient-text leading-tight text-center md:text-left">
                Creating Magical Horse Riding Experiences
              </h2>
              <p className="text-lg text-stables-700 leading-relaxed text-center md:text-left">
                At The Stables, we offer a unique and magical horse riding
                experience for the whole family. Our rides are perfect for
                introducing young children to the wonderful world of horse
                riding. Our experienced and friendly staff are dedicated to
                ensuring that all our visitors have a fun and educational
                experience. Come and join us and make unforgettable memories
                with your loved ones.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-stables-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stables-800 mb-1">
                      Expert Guides
                    </h4>
                    <p className="text-stables-600 text-sm">
                      Certified instructors with years of experience
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-stables-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stables-800 mb-1">
                      Gentle Horses
                    </h4>
                    <p className="text-stables-600 text-sm">
                      Well-trained, patient horses for all ages
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-stables-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stables-800 mb-1">
                      Safety First
                    </h4>
                    <p className="text-stables-600 text-sm">
                      Comprehensive safety equipment and protocols
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-stables-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stables-800 mb-1">
                      Beautiful Location
                    </h4>
                    <p className="text-stables-600 text-sm">
                      Scenic trails through natural countryside
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Link
                  to="/about"
                  className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 inline-flex items-center"
                >
                  Learn More About Us
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
                <a
                  href="https://wa.me/27699464074?text=Hi! I'd like to learn more about The Stables."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-stables-600 text-stables-600 hover:bg-stables-600 hover:text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 inline-flex items-center"
                >
                  Contact Us
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-2 gap-4 scroll-reveal-right overflow-hidden sm:overflow-visible rounded-2xl sm:rounded-none p-2 sm:p-0">
              <div className="space-y-4">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-lg sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                  <img
                    src="horseInStable.jpg"
                    alt="Horse riding experience"
                    className="relative rounded-lg shadow-2xl w-full h-64 object-cover sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300"
                  />
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-lg sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                  <img
                    src="outride1.jpg"
                    alt="Family horse riding"
                    className="relative rounded-lg shadow-2xl w-full h-48 sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-lg sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                  <img
                    src="sunset1.jpg"
                    alt="Scenic trail ride"
                    className="relative rounded-lg shadow-2xl w-full h-48 sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300"
                  />
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-lg sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                  <img
                    src="Stables2.jpg"
                    alt="Horse care and interaction"
                    className="relative rounded-lg shadow-2xl w-full h-64 object-cover sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider topColor="#ffffff" bottomColor="#3a5a3a" />

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-stables-700 to-stables-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold font-serif gradient-text-gold mb-6">
              What Our Visitors Say
            </h2>
            <p className="text-xl text-stables-200 max-w-3xl mx-auto">
              Hear from families who have experienced the magic of The Stables
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { initial: "S", name: "Sarah Johnson", type: "Family Visit", quote: "Amazing experience for our kids! The ponies were so gentle and the staff were incredibly patient. Our 5-year-old loved every minute of her ride. We'll definitely be back!" },
              { initial: "M", name: "Mike & Lisa", type: "Romantic Picnic", quote: "The romantic picnic package was perfect for our anniversary. Beautiful scenery, delicious food, and such a peaceful experience. Highly recommend!" },
              { initial: "D", name: "David Thompson", type: "Birthday Party", quote: "We celebrated my daughter's 8th birthday here and it was absolutely perfect! The party package included everything and the kids had a blast. Great memories made!" },
            ].map(({ initial, name, type, quote }, i) => (
              <div key={i} className={`group relative scroll-reveal stagger-${i + 1}`}>
                <div className="absolute inset-0 bg-gradient-to-r from-earth-500 to-earth-600 rounded-3xl sm:rotate-1 sm:group-hover:rotate-2 transition-transform duration-300" />
                <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <svg key={s} className="w-4 h-4 text-earth-500 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-stables-700 leading-relaxed mb-6 italic">
                    "{quote}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-stables-500 to-stables-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4 flex-shrink-0">
                      {initial}
                    </div>
                    <div>
                      <h4 className="font-semibold text-stables-800">{name}</h4>
                      <p className="text-stables-600 text-sm">{type}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider topColor="#2f4a2f" bottomColor="#ffffff" flip />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start text-center md:text-left">
            {/* FAQ Content */}
            <div className="scroll-reveal-left">
              <div className="inline-block bg-stables-100 text-stables-800 px-4 py-2 mb-6 rounded-full text-sm font-semibold tracking-wide">
                FAQ
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-serif gradient-text mb-6 text-center md:text-left">
                Find Unbridled Joy
              </h2>
              <p className="text-lg text-stables-700 mb-8 leading-relaxed text-center md:text-left">
                Whether you're a seasoned equestrian or a complete beginner, our
                services offers a welcoming and inclusive environment for riders
                of all levels.
              </p>

              <div className="space-y-4">
                {faqItems.slice(0, 6).map((item, index) => (
                  <div
                    key={index}
                    className="border border-stables-200 rounded-lg"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-stables-50 transition-colors duration-200"
                    >
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-stables-600 rounded-full mr-3"></div>
                        <span className="font-semibold text-stables-800">
                          {item.question}
                        </span>
                      </div>
                      <svg
                        className={`w-5 h-5 text-stables-600 transform transition-transform duration-200 ${
                          openFAQ === index ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {openFAQ === index && (
                      <div className="px-6 pb-4">
                        <p className="text-stables-700">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link
                  to="/faq"
                  className="inline-flex items-center text-stables-600 hover:text-stables-700 font-semibold transition-colors duration-200"
                >
                  View All Questions
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="relative group scroll-reveal-right">
              <div className="absolute inset-0 bg-gradient-to-r from-earth-500 to-earth-600 rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
              <img
                src="faqImage.jpg"
                alt="Woman riding horse outdoors"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300"
              />
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-stables-500 to-stables-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-stables-500 to-stables-600 rounded-full group-hover:scale-110 transition-transform duration-300"></div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider topColor="#ffffff" bottomColor="#4a6f4a" />

      {/* Contact Us Section */}
      <section className="py-20 bg-gradient-to-br from-stables-600 to-stables-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold font-serif gradient-text-gold mb-6">Get In Touch</h2>
            <p className="text-xl text-stables-200 max-w-3xl mx-auto">
              Ready to start your horse riding adventure? Contact us today to
              book your experience
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="group">
              <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <h3 className="text-2xl font-bold text-stables-800 mb-6">
                  Send Us a Message
                </h3>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-stables-700 mb-2"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                        placeholder="Your first name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-stables-700 mb-2"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                        placeholder="Your last name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-stables-700 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-stables-700 mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                        placeholder="+27 69 946 4074"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm font-medium text-stables-700 mb-2"
                    >
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                    >
                      <option value="">Select a service</option>
                      <option value="pony-rides">Pony Rides For Kids</option>
                      <option value="mini-ride">
                        Mini Ride For Adult & Kids
                      </option>
                      <option value="standard-ride">Standard Ride</option>
                      <option value="romantic-picnic">Romantic Picnic</option>
                      <option value="everyday-picnic">Every Day Picnic</option>
                      <option value="parties">Stable Parties</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-stables-700 mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      className="w-full px-4 py-3 border border-stables-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stables-500 focus:border-transparent bg-white"
                      placeholder="Tell us about your horse riding experience or any questions you have..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === "sending"}
                    className="w-full bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white py-3 rounded-lg text-base font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                  >
                    {formStatus === "sending" ? "Sending..." : "Send Message"}
                  </button>
                  {formStatus === "success" && (
                    <p role="status" className="text-green-700 text-sm font-medium">
                      Thank you! Your message has been sent. We'll be in touch soon.
                    </p>
                  )}
                  {formStatus === "error" && (
                    <p role="alert" className="text-red-600 text-sm font-medium">
                      Something went wrong. Please try again or WhatsApp us on +27 69 946 4074.
                    </p>
                  )}
                </form>
              </div>
            </div>

            {/* Contact Information & Map */}
            <div className="space-y-8">
              {/* Contact Info */}
              <div className="group">
                <div className="bg-white/15 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Contact Information
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="bg-gradient-to-r from-stables-500 to-stables-600 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">Call Us</h4>
                        <p className="text-stables-200">+27 69 946 4074</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-gradient-to-r from-stables-500 to-stables-600 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">Email Us</h4>
                        <p className="text-stables-200">
                          info@thestableshorserides.co.za
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-gradient-to-r from-stables-500 to-stables-600 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">Visit Us</h4>
                        <p className="text-stables-200">
                          The Stables, R964, Pretoria East, Boschkop
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="group">
                <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28739.572603251956!2d28.42557448215069!3d-25.78883724939969!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e955bbdfc0c3077%3A0x26b866ee84a006d8!2sThe%20Stables%20-%20Horse%20Rides!5e0!3m2!1sen!2sza!4v1777998768422!5m2!1sen!2sza"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="The Stables location"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
